import { useCallback } from "react";

import { useLensHistory } from "./useLensHistory";
import { ArtifactData, TestLensConfig, useLensTest } from "./useLensTest";

type TestLensParams = {
  config: TestLensConfig;
  artifactData: ArtifactData;
  interactionId?: string;
  tenantId?: string;
  tenantName?: string;
};

interface UseLensTestWithHistoryReturn {
  testResults: unknown;
  isLoading: boolean;
  error: string | null;
  testLens: (params: TestLensParams) => Promise<void>;
  clearResults: () => void;
  currentHistoryItem: ReturnType<typeof useLensHistory>["currentHistoryItem"];
  navigationInfo: ReturnType<typeof useLensHistory>["navigationInfo"];
  navigateHistory: ReturnType<typeof useLensHistory>["navigateHistory"];
}

const useLensTestWithHistory = (): UseLensTestWithHistoryReturn => {
  const lensTest = useLensTest();
  const lensHistory = useLensHistory();

  const testLens = useCallback(async (
    params: TestLensParams
  ): Promise<void> => {
    const { config, artifactData, interactionId, tenantId, tenantName } = params;
    const result = await lensTest.testLens(config, artifactData);

    if (result) {
      await lensHistory.addToHistory(
        {
          content: config.testLens,
          model: config.model,
          temperature: config.temperature,
          tokens: config.tokens,
          result,
          lensName: config.lensName,
          interactionId,
          tenantId,
          tenantName
        }
      );
    }
  }, [lensTest, lensHistory]);

  // Use results from current history item if available, otherwise use fresh test results
  const effectiveTestResults = lensHistory.currentHistoryItem?.lastResult ?? lensTest.testResults;

  return {
    testResults: effectiveTestResults, //lensTest.testResults,
    isLoading: lensTest.isLoading,
    error: lensTest.error,
    testLens,
    clearResults: lensTest.clearResults,
    currentHistoryItem: lensHistory.currentHistoryItem,
    navigationInfo: lensHistory.navigationInfo,
    navigateHistory: lensHistory.navigateHistory
  };
};

export { useLensTestWithHistory };
